import dayjs from "dayjs";
import { getFileExtension } from "@/utils";
import { getAliyunOssToken } from "holopix-api";
import { nanoid } from "nanoid";

type AliyunOptions = {
  file: File;
  onSuccess?(val: { url: string }): void;
  onError?(err: any): void;
  onProgress?(data: { percent: number; [key: string]: any }): void;
  namescape?: string;
  customPath?: string;
  customFileName?: string;
  /** OSS 上传目录，提供后会拼接到 customPath 前面 */
  dir?: string;
};

interface OssSignInfo {
  accessid: string;
  domain: string;
  policy: string;
  signature: string;
  host: string;
  callback: string;
  expire: string;
  dir: string;
}

class UploadFailedError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'UploadFailedError';
    this.status = status;
  }
}

const OSS_POLICY_EXPIRED_STATUS = 403;
const OSS_UPLOAD_MAX_RETRIES = 3;

/**
 * 获取文件的 MIME 类型
 * @param file 文件对象
 * @returns MIME 类型或默认值
 */
const getMimeType = (file: File): string => {
  return file.type || 'application/octet-stream';
};

/**
 * 根据 MIME 类型推断常见图片扩展名
 */
const guessExtByMime = (mime: string): string => {
  const map: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif'
  };
  return map[mime] || 'bin';
};

/**
 * 路径拼接
 * @param segments 路径片段
 * @returns 拼接后的路径
 */
const joinPath = (...segments: (string | undefined | null)[]): string => {
  return segments
    .filter(segment => segment != null && segment !== '') // 过滤空值
    .map(segment => String(segment).replace(/^\/+|\/+$/g, '')) // 去除首尾斜杠
    .filter(segment => segment !== '') // 再次过滤空字符串
    .join('/'); // 用斜杠连接
};

/**
 * 生成文件路径和文件名
 * @param file 文件对象
 * @param dir 上传目录
 * @param customPath 自定义路径
 * @param customFileName 自定义文件名
 * @param namescape 命名空间
 * @returns 完整的文件上传路径
 */
const generateFilePath = (
  file: File, 
  dir: string, 
  customPath?: string, 
  customFileName?: string, 
  namescape?: string,
  /** OSS 上传目录，提供后会拼接到 customPath 前面 */
  dirOverride?: string,
): string => {
  // 如果提供了自定义路径和文件名，直接使用（customPath 拼在 dir 之下作为子路径）
  if (customPath && customFileName) {
    if (dirOverride) return joinPath(dirOverride, customPath, customFileName);
    return joinPath(dir, customPath, customFileName);
  }
  
  // 如果只提供了自定义文件名，使用默认路径
  if (customFileName) {
    return joinPath(dir, dayjs().format("YYYYMMDD"), namescape, customFileName);
  }
  
  // 如果只提供了自定义路径，生成唯一文件名（customPath 拼在 dir 之下作为子路径）
  if (customPath) {
    const extension = getFileExtension(file.name);
    const fileName = `${Date.now()}-${nanoid()}.${extension}`;
    if (dirOverride) return joinPath(dirOverride, customPath, fileName);
    return joinPath(dir, customPath, fileName);
  }
  
  // 默认行为：生成唯一的文件路径和文件名
  const extension = getFileExtension(file.name);
  const fileName = `${Date.now()}-${nanoid()}.${extension}`;
  return joinPath(dir, dayjs().format("YYYYMMDD"), namescape, fileName);
};

/**
 * 创建上传表单数据
 * @param file 文件对象
 * @param signInfo OSS 签名信息
 * @param namescape 命名空间
 * @param customPath 自定义路径
 * @param customFileName 自定义文件名
 * @returns FormData 上传表单
 */
const createFormData = (
  file: File,
  signInfo: {
    accessid: string;
    policy: string;
    signature: string;
    dir: string;
    callback: string;
  },
  namescape?: string,
  customPath?: string,
  customFileName?: string,
  dirOverride?: string,
): FormData => {
  // 使用新的默认路径格式
  const defaultDir = signInfo.dir || 'pino/uploads';
  const filePath = generateFilePath(file, defaultDir, customPath, customFileName, namescape, dirOverride);
  const formData = new FormData();

  formData.append('key', filePath);
  formData.append('OSSAccessKeyId', signInfo.accessid);
  formData.append('policy', signInfo.policy);
  formData.append('signature', signInfo.signature);
  formData.append("callback", signInfo.callback);
  formData.append('success_action_status', '200');

  const mimeType = getMimeType(file);
  formData.append('Content-Type', mimeType);

  if (file.name.endsWith('.gz')) {
    formData.append('Content-Encoding', 'gzip');
  }

  formData.append('file', file);
  return formData;
};

/**
 * 上传文件并支持重试
 * @param form 上传表单
 * @param signInfo OSS 主机和域名信息
 * @param retries 剩余重试次数
 * @param onProgress 进度回调
 * @returns 上传文件的 URL
 */
const uploadWithRetry = async (
  form: FormData,
  signInfo: { host: string; domain: string },
  retries: number,
  onProgress?: (data: { percent: number }) => void
): Promise<string> => {
  try {
    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress({ percent });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const url = `${signInfo.domain}/${form.get('key')}`;
          resolve(url);
        } else {
          reject(new UploadFailedError(`Upload failed with status ${xhr.status}`, xhr.status));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new UploadFailedError('Upload failed'));
      });

      xhr.open('POST', signInfo.host);
      xhr.send(form);
    });
  } catch (error) {
    if (retries > 0) {
      console.warn(`Upload failed, retrying... (${retries} retries left)`);
      return uploadWithRetry(form, signInfo, retries - 1, onProgress);
    } else {
      throw new Error(`Failed to upload after retries: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
};

/** OSS token 缓存：policy 过期前复用，减少扩图 prep 多次上传的 token 请求 */
let cachedOssToken: OssSignInfo | null = null;
let ossTokenFetchPromise: Promise<OssSignInfo> | null = null;

const OSS_TOKEN_REFRESH_BUFFER_SEC = 60;

/**
 * 获取 OSS 签名（带缓存）
 */
const fetchOssToken = async (force = false): Promise<OssSignInfo> => {
  const nowSec = Math.floor(Date.now() / 1000);

  if (!force && cachedOssToken?.expire) {
    const expireSec = Number(cachedOssToken.expire);
    if (!Number.isNaN(expireSec) && expireSec - nowSec > OSS_TOKEN_REFRESH_BUFFER_SEC) {
      return cachedOssToken;
    }
  }

  if (!ossTokenFetchPromise || force) {
    ossTokenFetchPromise = (getAliyunOssToken() as unknown as Promise<OssSignInfo>)
      .then((signInfo) => {
        cachedOssToken = signInfo;
        return signInfo;
      })
      .finally(() => {
        ossTokenFetchPromise = null;
      });
  }

  return ossTokenFetchPromise;
};

/**
 * 预取 OSS token（扩图上传原图/蒙版前调用，降低首次上传延迟）
 */
export const prefetchAliyunOssToken = (): Promise<OssSignInfo> => fetchOssToken();

/**
 * 主动失效 OSS token 缓存（登出或上传鉴权失败时调用）
 */
export const invalidateAliyunOssToken = (): void => {
  cachedOssToken = null;
};

/**
 * 使用已有签名上传单个文件
 */
const uploadFileWithSignInfo = async (
  file: File,
  signInfo: OssSignInfo,
  options?: Pick<AliyunOptions, "onProgress" | "namescape" | "customPath" | "customFileName" | "dir">,
  tokenRefreshRetries = OSS_UPLOAD_MAX_RETRIES
): Promise<string> => {
  const { onProgress, namescape, customPath, customFileName, dir } = options || {};
  const formData = createFormData(file, signInfo, namescape, customPath, customFileName, dir);

  try {
    return await uploadWithRetry(formData, signInfo, OSS_UPLOAD_MAX_RETRIES, onProgress);
  } catch (error) {
    const shouldRefreshToken =
      tokenRefreshRetries > 0 &&
      error instanceof UploadFailedError &&
      error.status === OSS_POLICY_EXPIRED_STATUS;

    if (shouldRefreshToken) {
      invalidateAliyunOssToken();
      const freshSignInfo = await fetchOssToken(true);
      return uploadFileWithSignInfo(file, freshSignInfo, options, tokenRefreshRetries - 1);
    }

    throw error;
  }
};

/**
 * 上传单个文件并返回Promise
 */
export const uploadFilePromise = async (
  file: File,
  options?: Pick<AliyunOptions, "onProgress" | "namescape" | "customPath" | "customFileName" | "dir">
): Promise<string> => {
  const signInfo = await fetchOssToken();
  return uploadFileWithSignInfo(file, signInfo, options);
};

/**
 * 批量上传文件（单次 token，并行上传）
 */
export const uploadFileList = async (
  fileList: File[],
  options?: Pick<AliyunOptions, "onProgress" | "namescape" | "customPath" | "customFileName">
): Promise<string[]> => {
  if (fileList.length === 0) {
    return [];
  }

  const signInfo = await fetchOssToken();

  return Promise.all(
    fileList.map((file) => uploadFileWithSignInfo(file, signInfo, options))
  );
};

/**
 * 将 Blob 上传到阿里云 OSS（前端直传，需服务端签名）
 * @param blob 本地 Blob 数据
 * @param options 额外上传配置，支持自定义路径/文件名/命名空间/进度回调
 * @returns OSS 访问 URL
 */
export const uploadBlobToAliyun = async (
  blob: Blob,
  options?: (Pick<AliyunOptions, 'onProgress' | 'namescape' | 'customPath' | 'customFileName' | 'dir'>) & { filename?: string }
): Promise<string> => {
  const mime = blob.type || 'application/octet-stream';
  const ext = guessExtByMime(mime);
  const fileName = options?.filename ?? `blob-${Date.now()}-${nanoid()}.${ext}`;
  const file = new File([blob], fileName, { type: mime });

  return uploadFilePromise(file, {
    onProgress: options?.onProgress,
    namescape: options?.namescape,
    customPath: options?.customPath,
    customFileName: options?.customFileName,
    dir: options?.dir,
  });
};

/**
 * 将任意可 fetch 的 URL 对应的图片上传到阿里云 OSS
 * @param url 可访问的图片 URL
 * @param options 额外上传配置
 * @returns OSS 访问 URL
 */
export const uploadFileUrlToAliyun = async (
  url: string,
  options?: (Pick<AliyunOptions, 'onProgress' | 'namescape' | 'customPath' | 'customFileName' | 'dir'>) & { filename?: string }
): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch blob url: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  return uploadBlobToAliyun(blob, options);
};